import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import AudioPlayer from "@/components/AudioPlayer";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Plus,
  Music,
  Coins,
  Crown,
  Loader2,
  Download,
  Clock,
  Zap,
} from "lucide-react";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [jingles, setJingles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState(300);
  const [topupLoading, setTopupLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/jingles");
      setJingles(data.jingles || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    refresh();
  }, []);

  const openDetail = async (j) => {
    setDetailLoading(true);
    setSelected({ ...j, audio_url: null });
    try {
      const { data } = await apiClient.get(`/jingles/${j.jingle_id}`);
      setSelected(data);
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  };

  const download = async (jingleId, fmt = "mp3") => {
    const res = await apiClient.get(`/jingles/${jingleId}/audio`, {
      params: { format: fmt },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `jingle_${jingleId}.${fmt}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleTopup = async () => {
    if (topupAmount < 300) {
      toast.error("Minimum top-up is ₹300 (15 credits).");
      return;
    }
    setTopupLoading(true);
    try {
      await openRazorpay({
        purpose: "topup",
        amount: Number(topupAmount),
        onSuccess: () => {
          setTopupOpen(false);
          refresh();
        },
      });
    } finally {
      setTopupLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-page">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2">
              Dashboard
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter">
              {user ? `Hey, ${user.name.split(" ")[0]}.` : "Welcome."}
            </h1>
          </div>
          <Link to="/generate" data-testid="dashboard-new-jingle-link">
            <Button
              className="bg-[#FF4500] hover:bg-[#E03C00] text-white rounded-sm h-11 px-5"
              data-testid="dashboard-new-jingle-btn"
            >
              <Plus size={18} className="mr-2" /> New jingle
            </Button>
          </Link>
        </div>

        {/* Control Room Grid */}
        <div className="grid grid-cols-12 gap-4 mb-10">
          <div
            className="col-span-12 md:col-span-4 bg-[#F8F9FA] border border-black/10 p-6"
            data-testid="stat-credits"
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 font-bold mb-4">
              <Coins size={14} className="text-[#FF4500]" /> Credits
            </div>
            <div className="font-display text-5xl font-semibold tabular-nums">
              {user?.credits ?? "—"}
            </div>
            <div className="text-xs text-zinc-500 mt-2 mb-4">
              1 credit = 1 second of music · enough for ~{user?.credits ? Math.floor(user.credits / 30) : 0} × 30-sec jingles
            </div>
            <button
              onClick={() => setTopupOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-[#FF4500] hover:underline"
              data-testid="topup-btn"
            >
              <Zap size={12} /> Top up credits
            </button>
          </div>
          <div
            className="col-span-6 md:col-span-4 bg-[#F8F9FA] border border-black/10 p-6"
            data-testid="stat-jingles"
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 font-bold mb-4">
              <Music size={14} className="text-[#FF4500]" /> Jingles
            </div>
            <div className="font-display text-5xl font-semibold tabular-nums">
              {jingles.length}
            </div>
            <div className="text-xs text-zinc-500 mt-2">Total generated</div>
          </div>
          <div
            className="col-span-6 md:col-span-4 bg-[#09090B] text-white p-6"
            data-testid="stat-plan"
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 font-bold mb-4">
              <Crown size={14} className="text-[#FF4500]" /> Plan
            </div>
            <div className="font-display text-3xl md:text-4xl font-semibold capitalize">
              {user?.subscription_tier || "Free"}
            </div>
            <Link
              to="/pricing"
              className="inline-block mt-3 text-xs uppercase tracking-widest text-[#FF4500] font-bold hover:underline"
              data-testid="upgrade-plan-link"
            >
              Upgrade →
            </Link>
          </div>
        </div>

        {/* Jingles list */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-display text-2xl font-semibold tracking-tight">
            Your jingles
          </h2>
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">
            {jingles.length} total
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center text-zinc-500">
            <Loader2 className="animate-spin mr-2" size={18} /> Loading…
          </div>
        ) : jingles.length === 0 ? (
          <div
            className="py-20 border border-dashed border-black/20 flex flex-col items-center justify-center text-center"
            data-testid="empty-state"
          >
            <div className="w-14 h-14 bg-[#F8F9FA] border border-black/10 flex items-center justify-center mb-5">
              <Music size={22} className="text-zinc-400" />
            </div>
            <div className="font-display text-2xl font-semibold mb-2">
              No jingles yet.
            </div>
            <div className="text-zinc-500 mb-6 max-w-sm">
              Create your first jingle from a script or AI-generate one in seconds.
            </div>
            <Button
              onClick={() => navigate("/generate")}
              className="bg-[#FF4500] hover:bg-[#E03C00] text-white rounded-sm h-11 px-5"
              data-testid="empty-state-cta"
            >
              Generate first jingle
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {jingles.map((j) => (
              <button
                key={j.jingle_id}
                onClick={() => openDetail(j)}
                className="text-left bg-white border border-black/10 p-5 card-hover"
                data-testid={`jingle-card-${j.jingle_id}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-black text-white">
                    {j.module}
                  </span>
                  <div className="flex items-center gap-1 text-xs text-zinc-500">
                    <Clock size={12} /> {j.duration_sec}s
                  </div>
                </div>
                <div className="font-display text-lg font-semibold leading-snug mb-2 line-clamp-2">
                  {j.title}
                </div>
                <div className="text-sm text-zinc-500 line-clamp-2 mb-4">
                  {j.hook_line}
                </div>
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                  <span>{j.language}</span>
                  <span className="w-1 h-1 bg-zinc-300 rounded-full" />
                  <span>{j.voice}</span>
                  <span className="w-1 h-1 bg-zinc-300 rounded-full" />
                  <span>{(j.genre || j.style || "").replace("_", " ")}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl rounded-sm p-0 border-black/10" data-testid="jingle-detail-dialog">
          {selected && (
            <div className="p-6 md:p-8">
              <DialogHeader className="mb-4">
                <DialogTitle className="font-display text-2xl font-semibold tracking-tight">
                  {selected.title}
                </DialogTitle>
              </DialogHeader>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-5">
                <span className="px-2 py-1 bg-black text-white">{selected.module}</span>
                <span>{selected.duration_sec}s</span>
                <span>·</span>
                <span>{selected.language}</span>
                <span>·</span>
                <span>{selected.voice}</span>
                <span>·</span>
                <span>{(selected.genre || selected.style || "").replace("_", " ")}</span>
                {selected.mood && (<><span>·</span><span>{selected.mood}</span></>)}
              </div>

              {detailLoading || !selected.audio_url ? (
                <div className="py-8 flex items-center justify-center text-zinc-500">
                  <Loader2 className="animate-spin mr-2" size={16} /> Loading audio…
                </div>
              ) : (
                <AudioPlayer
                  audioUrl={selected.audio_url}
                  onDownload={() => download(selected.jingle_id, "mp3")}
                />
              )}

              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-3">
                  Lyrics
                </div>
                <pre className="whitespace-pre-wrap font-display text-lg leading-relaxed bg-[#F8F9FA] border border-black/10 p-5 max-h-60 overflow-auto" data-testid="jingle-lyrics">
{selected.lyrics}
                </pre>
              </div>

              <div className="mt-6 flex gap-3">
                <Button
                  onClick={() => download(selected.jingle_id, "mp3")}
                  className="bg-black hover:bg-zinc-800 text-white rounded-sm h-10 px-4"
                  data-testid="download-mp3-btn"
                >
                  <Download size={16} className="mr-2" /> MP3
                </Button>
                <Button
                  onClick={() => download(selected.jingle_id, "wav")}
                  variant="outline"
                  className="rounded-sm h-10 px-4 border-black/20"
                  data-testid="download-wav-btn"
                >
                  <Download size={16} className="mr-2" /> WAV
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Top-up Dialog */}
      <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
        <DialogContent className="max-w-md rounded-sm p-0 border-black/10" data-testid="topup-dialog">
          <div className="p-6">
            <DialogHeader className="mb-1">
              <DialogTitle className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
                <Zap size={20} className="text-[#FF4500]" />
                Top up credits
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm text-zinc-500 mb-5">
              Pay any amount ≥ ₹300 and get credits at ₹20 each. No subscription required.
            </div>
            <div className="bg-[#F8F9FA] border border-black/10 p-4 mb-4">
              <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Amount (₹)</Label>
              <Input
                type="number"
                min={300}
                step={50}
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                className="mt-2 rounded-sm border-black/20 text-lg font-semibold h-11"
                data-testid="topup-amount-input"
              />
              <div className="flex gap-2 mt-3">
                {[300, 500, 1000, 2000].map((a) => (
                  <button
                    key={a}
                    onClick={() => setTopupAmount(a)}
                    className={`flex-1 px-2 py-1.5 border text-xs font-semibold ${
                      Number(topupAmount) === a
                        ? "bg-black text-white border-black"
                        : "bg-white border-black/20 hover:border-black"
                    }`}
                    data-testid={`topup-preset-${a}`}
                  >
                    ₹{a}
                  </button>
                ))}
              </div>
              <div className="h-px bg-black/10 my-4" />
              <div className="flex justify-between items-end">
                <span className="text-sm text-zinc-500">You'll receive</span>
                <span className="font-display text-2xl font-semibold tracking-tighter tabular-nums">
                  {Math.floor(Number(topupAmount) / 20)} credits
                </span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono mt-1 text-right">
                ≈ {Math.floor(Number(topupAmount) / 20 / 15)} × 15s · {Math.floor(Number(topupAmount) / 20 / 30)} × 30s jingles
              </div>
            </div>
            <Button
              onClick={handleTopup}
              disabled={topupLoading || Number(topupAmount) < 300}
              className="w-full bg-[#FF4500] hover:bg-[#E03C00] text-white rounded-sm h-11 font-semibold disabled:opacity-50"
              data-testid="topup-confirm-btn"
            >
              {topupLoading ? (
                <><Loader2 size={16} className="mr-2 animate-spin" /> Opening Razorpay…</>
              ) : (
                `Pay ₹${topupAmount}`
              )}
            </Button>
            <div className="mt-3 text-center font-mono text-[10px] uppercase tracking-widest text-zinc-400">
              🔒 Secured by Razorpay · UPI · Cards · Netbanking
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
